// The result screen's two mount-time reads: fresh download links for the
// applicant's own documents, and the Program Brochure.
//
// ── WHY THE LINKS ARE RE-FETCHED AT ALL ──────────────────────────────────────
//
// A document link is a 15-minute blob SAS. The finalize response carries those
// URLs, the funnel writes the whole response into sessionStorage, and this screen
// re-rendered them from that blob on every visit and every reload. A finalized
// submission is not retrievable by the applicant and there was no anonymous
// re-fetch endpoint, so every download button here was dead a quarter of an hour
// after the person finished — and still offered, with no error and nothing to
// explain it. That defect is PRE-EXISTING; this hook is what closes it.
//
// ── TWO CALLS, NOT ONE, AND THE SPLIT IS DELIBERATE ──────────────────────────
//
// The documents are per-submission and ride the result token. The brochure is ONE
// Phoenix-wide file, identical for every applicant, on its own untokened endpoint.
// Folding it behind the token would make a public marketing document unreachable
// on exactly the pages whose token has lapsed — which is the case this hook
// exists for. They also fail independently: an expired token must not cost the
// brochure, and a role with no brochure must not cost the documents.
//
// ── FAILURE IS ALWAYS "KEEP WHAT YOU HAVE" ───────────────────────────────────
//
// Neither read has an error state, on purpose. The documents call falls back to
// the stored links — the screen's own previous behaviour, and better than an error
// banner over links that might still work. The brochure call falls back to no
// brochure, which is indistinguishable from the (common) case of none uploaded.
// A 404, a 429, a parse failure and an outage all land in the same place.

import { useEffect, useState } from "react";
import {
  prequalificationApi,
  type PrequalDocumentLink,
  type PrequalRole,
} from "../../api/prequalification";

/**
 * Fresh links for the applicant's own documents, or null while pending / when the
 * token does not resolve.
 *
 * Returns null — never an empty array — for "no fresher links than the ones you
 * hold". An empty array would read as "this submission has no documents" and blank
 * the cards; the caller's contract is that null means *fall back*.
 */
export function useFreshDocumentLinks(
  resultToken: string | null | undefined,
): PrequalDocumentLink[] | null {
  const [links, setLinks] = useState<PrequalDocumentLink[] | null>(null);

  useEffect(() => {
    // No token: state persisted before the token existed, or a caller that
    // withheld it — the result screen does that on a decline, which has documents
    // but no view to render them in. Nothing to ask with, and nothing to report:
    // the stored links stand.
    if (!resultToken) {
      setLinks(null);
      return;
    }

    // Guards the async write against a token change and against unmount. Both are
    // real here: React StrictMode double-fires this effect in development, and the
    // screen re-renders on the ceremony → documents view switch.
    let live = true;
    prequalificationApi
      .resultDocuments(resultToken)
      .then((fresh) => {
        // An empty list from the server cannot happen (the endpoint 404s rather
        // than returning one), but treating it as "no fresher links" rather than
        // as "no documents" keeps the fallback contract true if that ever changes.
        if (live) setLinks(fresh.documents.length > 0 ? fresh.documents : null);
      })
      .catch(() => {
        if (live) setLinks(null);
      });

    return () => {
      live = false;
    };
  }, [resultToken]);

  return links;
}

/**
 * The role's Program Brochure URL, or null when none is uploaded / the read
 * failed. Null is what hides the fourth card — no placeholder, no broken link,
 * no error.
 *
 * `enabled` exists because a hook cannot be called conditionally but a SOFT
 * DECLINE has no documents view to put a card in, and firing an anonymous
 * rate-limited read for a screen that will not render its answer is noise. The
 * sibling brand hook holds the same line ("asks the network nothing when the
 * record says there is no site").
 */
export function useProgramBrochureUrl(role: PrequalRole, enabled = true): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setUrl(null);
      return;
    }

    let live = true;
    prequalificationApi
      .programBrochureLink(role)
      .then((link) => {
        // `available` AND a non-empty url. The server sets them together, but the
        // card needs a URL to open and a truthy flag beside a null url would
        // render a button that does nothing — the one thing the shared standard is
        // explicit about.
        const resolved = link.available && link.url ? link.url : null;
        if (live) setUrl(resolved);
      })
      .catch(() => {
        if (live) setUrl(null);
      });

    return () => {
      live = false;
    };
  }, [role, enabled]);

  return url;
}
